/**
 * SoleadLab 2.0 - Schema Parser Service
 *
 * Main thread API for the JSON schema parser WebWorker.
 * Provides a Promise-based interface with progress callbacks.
 */

import type { ParsedSchema } from '@/types/monitoring';

// ============================================
// Types
// ============================================

interface WorkerResponse {
  type: 'result' | 'error' | 'progress';
  requestId: string;
  result?: ParsedSchema & {
    stats: {
      totalKeys: number;
      entitiesFound: number;
      metricsFound: number;
      durationMs: number;
    };
  };
  error?: string;
  progress?: number;
}

export interface ParseOptions {
  onProgress?: (progress: number) => void;
}

export interface ParseResult extends ParsedSchema {
  stats: {
    totalKeys: number;
    entitiesFound: number;
    metricsFound: number;
    durationMs: number;
  };
}

// ============================================
// Worker Singleton
// ============================================

let worker: Worker | null = null;
let requestCounter = 0;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../workers/schemaParser.worker.ts', import.meta.url),
      { type: 'module' }
    );
  }
  return worker;
}

/**
 * Terminate the worker (e.g., on page unmount).
 */
export function terminateParser(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
}

// ============================================
// Parse JSON (via WebWorker)
// ============================================

/**
 * Parse a JSON object (or string) to extract the entity schema.
 * Runs in a WebWorker to keep the UI thread responsive.
 *
 * @param jsonInput - Raw JSON object or JSON string to parse
 * @param options - Progress callback
 * @returns ParseResult with entities, detected provider, confidence, and stats
 */
export function parseJsonSchema(
  jsonInput: unknown,
  options?: ParseOptions
): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const w = getWorker();
    const requestId = `req_${++requestCounter}_${Date.now()}`;

    // Parse JSON string if needed
    let json: unknown;
    if (typeof jsonInput === 'string') {
      try {
        json = JSON.parse(jsonInput);
      } catch (err) {
        reject(new Error(`Invalid JSON: ${err instanceof Error ? err.message : 'Parse error'}`));
        return;
      }
    } else {
      json = jsonInput;
    }

    function handleMessage(event: MessageEvent<WorkerResponse>) {
      const { type, requestId: respId, result, error, progress } = event.data;

      if (respId !== requestId) return; // Not our request

      if (type === 'progress') {
        options?.onProgress?.(progress ?? 0);
        return;
      }

      // Clean up listener
      w.removeEventListener('message', handleMessage);

      if (type === 'error') {
        reject(new Error(error || 'Worker parsing failed'));
        return;
      }

      if (type === 'result' && result) {
        resolve(result);
        return;
      }

      reject(new Error('Unexpected worker response'));
    }

    w.addEventListener('message', handleMessage);

    // Send to worker
    w.postMessage({ type: 'parse', json, requestId });
  });
}

// ============================================
// Parse JSON (synchronous fallback — main thread)
// ============================================

/**
 * Synchronous fallback parser for environments where WebWorkers
 * aren't available (e.g., SSR, tests). Uses the same algorithm
 * as the worker but blocks the main thread.
 *
 * Prefer parseJsonSchema() for production use.
 */
export function parseJsonSchemaSync(json: unknown): ParseResult {
  const startTime = performance.now();

  if (typeof json === 'string') {
    json = JSON.parse(json);
  }

  if (typeof json !== 'object' || json === null) {
    return {
      entities: [],
      detectedProvider: null,
      rawCategories: [],
      confidence: 0,
      parsedAt: new Date().toISOString(),
      stats: { totalKeys: 0, entitiesFound: 0, metricsFound: 0, durationMs: 0 },
    };
  }

  // Inline version of the worker's parse logic
  const obj = json as Record<string, unknown>;
  const entitiesMap = new Map<string, { entity_id: string; entity_type: string; metrics: Array<{ key: string }> }>();
  const categories: string[] = [];

  const CATEGORY_MAP: Record<string, string> = {
    inverters: 'inverter', inverter: 'inverter',
    meters: 'meter', meter: 'meter',
    sensors: 'sensor', sensor: 'sensor',
    basics: 'basics', basic: 'basics',
    environmental: 'sensor',
    statuses: 'status', status: 'status',
    stringboxes: 'stringbox', batteries: 'battery',
    trackers: 'tracker', ppcs: 'ppc',
  };

  function isTimestamp(key: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(key) || /^\d{10,13}$/.test(key);
  }

  function isMeasurement(o: unknown): boolean {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return false;
    return Object.values(o).every((v) => v === null || typeof v === 'number' || typeof v === 'string');
  }

  for (const [catKey, catVal] of Object.entries(obj)) {
    if (typeof catVal !== 'object' || catVal === null) continue;
    const entityType = CATEGORY_MAP[catKey.toLowerCase()] || catKey;
    categories.push(catKey);

    for (const [tsKey, tsVal] of Object.entries(catVal as Record<string, unknown>)) {
      if (!isTimestamp(tsKey) || typeof tsVal !== 'object' || tsVal === null) continue;

      if (isMeasurement(tsVal)) {
        const eid = `${catKey}_site`;
        if (!entitiesMap.has(eid)) {
          entitiesMap.set(eid, { entity_id: eid, entity_type: entityType, metrics: [] });
        }
        const e = entitiesMap.get(eid)!;
        for (const mk of Object.keys(tsVal as Record<string, unknown>)) {
          if (!e.metrics.some((m) => m.key === mk)) e.metrics.push({ key: mk });
        }
        break;
      }

      for (const [eId, eData] of Object.entries(tsVal as Record<string, unknown>)) {
        if (!isMeasurement(eData)) continue;
        const mk = `${catKey}:${eId}`;
        if (!entitiesMap.has(mk)) {
          entitiesMap.set(mk, { entity_id: eId, entity_type: entityType, metrics: [] });
        }
        const e = entitiesMap.get(mk)!;
        for (const mKey of Object.keys(eData as Record<string, unknown>)) {
          if (!e.metrics.some((m) => m.key === mKey)) e.metrics.push({ key: mKey });
        }
      }
      break;
    }
  }

  const entities = Array.from(entitiesMap.values());
  entities.forEach((e) => e.metrics.sort((a, b) => a.key.localeCompare(b.key)));
  entities.sort((a, b) => a.entity_type.localeCompare(b.entity_type) || a.entity_id.localeCompare(b.entity_id));

  const metricsFound = entities.reduce((s, e) => s + e.metrics.length, 0);

  return {
    entities,
    detectedProvider: categories.some((c) => ['basics', 'inverters'].includes(c)) ? 'meteocontrol' : null,
    rawCategories: categories,
    confidence: entities.length > 0 ? 0.8 : 0.1,
    parsedAt: new Date().toISOString(),
    stats: {
      totalKeys: Object.keys(obj).length,
      entitiesFound: entities.length,
      metricsFound,
      durationMs: performance.now() - startTime,
    },
  };
}
